// DOM side of the interface language: loads dictionaries, applies static-markup bindings, persists
// the choice (localStorage before sign-in, PATCH /me after) and announces changes. Lookup,
// fallback, formatting and the reconcile rule live in assets/pure/i18n.js.
import { showToast } from "./shared/dom.js";

const i18n = window.TransportOSI18n;
const { t } = i18n;

const STORAGE_KEY = "transportos:locale";
const PENDING_KEY = "transportos:locale-pending";
const RETRY_DELAYS_MS = [400, 1200];

let saveToAccount = null;

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchDictionary(locale) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const res = await fetch(`assets/i18n/${locale}.json`, { cache: "no-store" });
      if (res.ok) return await res.json();
    } catch {
      /* retried below */
    }
    if (attempt >= RETRY_DELAYS_MS.length) return null;
    await wait(RETRY_DELAYS_MS[attempt]);
  }
}

async function ensureDictionary(locale) {
  if (i18n.hasDictionary(locale)) return true;
  const dictionary = await fetchDictionary(locale);
  if (dictionary) i18n.setDictionary(locale, dictionary);
  return Boolean(dictionary);
}

export function applyTranslations(scope = document) {
  scope.querySelectorAll("[data-i18n]").forEach((el) => {
    el.textContent = t(el.dataset.i18n);
  });
  scope.querySelectorAll("[data-i18n-attr]").forEach((el) => {
    i18n.parseAttrBindings(el.dataset.i18nAttr).forEach(({ attr, key }) => el.setAttribute(attr, t(key)));
  });
  // "Sent to <b>number</b>": the text on either side of a live value moves with the language.
  scope.querySelectorAll("[data-i18n-around]").forEach((el) => {
    const { before, after } = i18n.splitAround(t(el.dataset.i18nAround));
    const beforeEl = el.querySelector('[data-part="before"]');
    const afterEl = el.querySelector('[data-part="after"]');
    if (beforeEl) beforeEl.textContent = before;
    if (afterEl) afterEl.textContent = after;
  });
  scope.querySelectorAll("[data-set-locale]").forEach((btn) => {
    btn.setAttribute("aria-pressed", String(btn.dataset.setLocale === i18n.getLocale()));
  });
  document.documentElement.lang = i18n.intlLocale();
}

async function activate(locale) {
  const target = i18n.normalizeLocale(locale) || i18n.DEFAULT_LOCALE;
  if (!(await ensureDictionary(target))) return false;
  const changed = target !== i18n.getLocale();
  i18n.setLocale(target);
  writeStorage(STORAGE_KEY, target);
  applyTranslations();
  if (changed) window.dispatchEvent(new CustomEvent("transportos:locale-change", { detail: { locale: target } }));
  return true;
}

export async function initLocale() {
  if (["localhost", "127.0.0.1"].includes(location.hostname)) {
    i18n.onMissingKey((key, locale) => console.warn(`Missing ${locale} translation: ${key}`));
  }
  await ensureDictionary(i18n.DEFAULT_LOCALE);
  const stored = i18n.normalizeLocale(readStorage(STORAGE_KEY));
  if (stored && (await ensureDictionary(stored))) i18n.setLocale(stored);
  applyTranslations();
}

function persist(locale) {
  return saveToAccount(locale).then(
    () => writeStorage(PENDING_KEY, null),
    () => {
      writeStorage(PENDING_KEY, locale);
      showToast(t("locale.saveFailed"));
    },
  );
}

// Called once the signed-in user has a profile row. `save` writes the locale to the account.
export async function adoptAccountLocale(me, save) {
  saveToAccount = save;
  const { locale, save: needsSave } = i18n.reconcileLocale({
    serverLocale: me && me.locale,
    pendingLocale: readStorage(PENDING_KEY),
    deviceLocale: i18n.getLocale(),
  });
  if (!(await activate(locale))) return;
  if (needsSave) persist(locale);
  else writeStorage(PENDING_KEY, null);
}

export function releaseAccount() {
  saveToAccount = null;
}

async function chooseLocale(locale) {
  if (!(await activate(locale))) {
    showToast(t("locale.loadFailed"));
    return;
  }
  if (saveToAccount) persist(i18n.getLocale());
  else writeStorage(PENDING_KEY, i18n.getLocale());
}

export function wireLanguageSwitches() {
  document.addEventListener("click", (e) => {
    const button = e.target.closest("[data-set-locale]");
    if (!button) return;
    e.preventDefault();
    chooseLocale(button.dataset.setLocale);
  });
}
